import { memo, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
   Button,
   Div,
   PageHeader,
   SideMenu as SideMenuComponent,
   sideMenuControls,
   useMediaQuery,
   useTheme,
   type SideMenuItem,
} from "react-better-html";

import { devMode } from "../../constants/app";

import { useI18n } from "../../utils/i18n";
import { useReduxState, useSelectedOrganization } from "../../utils/hooks";

import SideBar from "./SideBar";

type SideMenuProps = {
   children?: React.ReactNode;
};

function SideMenu({ children }: SideMenuProps) {
   const theme = useTheme();
   const { t } = useI18n();
   const mediaQuery = useMediaQuery();
   const location = useLocation();
   const selectedOrganization = useSelectedOrganization();

   const user = useReduxState((state) => state.user.user);
   const workspacesList = useReduxState((state) => state.workspaces.list);

   const workspacesItems = useMemo<SideMenuItem[] | undefined>(
      () =>
         workspacesList?.map<SideMenuItem>((workspace) => ({
            type: "item",
            text: workspace.name,
            leftElement: <Div width={14} height={14} borderRadius={999} backgroundColor={workspace.color} />,
            href: `/workspaces/${workspace.id}`,
         })),
      [workspacesList],
   );
   const items = useMemo<SideMenuItem[]>(
      () => [
         {
            type: "divider",
            text: t((l) => l.components.sideMenu.main),
         },
         {
            type: "item",
            text: t((l) => l.components.sideMenu.dashboard),
            iconName: "squares",
            href: "/dashboard",
         },
         {
            hidden: selectedOrganization?.type !== "PERSONAL",
            type: "item",
            text: t((l) => l.components.sideMenu.trackedProperties),
            iconName: "house",
            href: "/properties",
            children: workspacesItems,
         },
         {
            hidden: selectedOrganization?.type !== "AGENCY",
            type: "item",
            text: t((l) => l.components.sideMenu.companyProperties),
            iconName: "house",
            href: "/properties",
         },
         {
            hidden: selectedOrganization?.type !== "AGENCY",
            type: "item",
            text: t((l) => l.components.sideMenu.workspaces),
            iconName: "folder",
            href: "/workspaces",
            children: workspacesItems,
         },
         {
            type: "item",
            text: t((l) => l.components.sideMenu.notifications),
            iconName: "bell",
            href: "/notifications",
         },
         {
            type: "item",
            hidden: !devMode,
            text: "Developer",
            iconName: "code",
            href: "/dev",
         },
      ],
      [t, selectedOrganization, workspacesItems],
   );
   const bottomItems = useMemo<SideMenuItem[]>(
      () => [
         {
            type: "item",
            text: t((l) => l.components.sideMenu.account),
            iconName: "user",
            href: "/account",
         },
         {
            type: "item",
            text: t((l) => l.components.sideMenu.settings),
            iconName: "gear",
            href: "/settings",
         },
         {
            type: "item",
            text: t((l) => l.components.sideMenu.support),
            iconName: "support",
            href: "/support",
         },
      ],
      [t],
   );

   return (
      <SideMenuComponent
         items={items}
         bottomItems={bottomItems}
         itemsAdditionalComponent={
            selectedOrganization ? (
               <Div marginInline={theme.styles.space}>
                  <PageHeader
                     title={selectedOrganization.name}
                     titleAs="h3"
                     titleFontSize={18}
                     description={t((l) => l.enums.organizationType[selectedOrganization.type])}
                     descriptionFontSize={14}
                     titleGap={theme.styles.gap / 4}
                     rightElement={
                        mediaQuery.size1000 ? (
                           <Button.icon icon="XMark" onClick={sideMenuControls.toggleOpened} />
                        ) : undefined
                     }
                     marginBottom={0}
                  />
               </Div>
            ) : undefined
         }
         sideBar={<SideBar />}
         location={location}
         isLoading={!user || !selectedOrganization}
      >
         {children}
      </SideMenuComponent>
   );
}

export default memo(SideMenu);
